import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { appState } from "$store";
import useCompanyForm from "$hooks/useCompanyForm";
import useAuth from "$hooks/useAuth";
import { queryClient } from "$utils/queryClient";
import { CompanyType } from "$types";
import { EDBService, CompanyServices } from "$services";
import { Fiability } from "$types/Fiability";
import { CacheInvalidationAction } from "$utils/cache";

export const useDockyardFormLogic = (
  dockyard?: any,
  defaultValuesForm?: any
) => {
  const [edbs, setEdbs] = useState([]);
  const [contributorOptions, setContributorOptions] = useState([]);

  const { filter: sidebarCompanyFilter } = useSelector(appState);
  const { isAdmin, isClient } = useAuth();

  const defaultValues = dockyard
    ? {
        ...dockyard,
        company: dockyard.company._id ? dockyard.company._id : dockyard.company,
        contact: dockyard.contact?._id || dockyard.project.contact?._id,
        project: dockyard.project._id ? dockyard.project._id : dockyard.project,
        interveningCompany:
          dockyard.interveningCompany?.map(({ _id }) => _id) || [],
        fiability: dockyard.fiability.toString(),
        approverId: dockyard.approver?.id || dockyard.approverId || null,
      }
    : {
        id: null,
        company: sidebarCompanyFilter?._id,
        project: null,
        code: null,
        codeAnalytics: null,
        name: null,
        address: null,
        address2: null,
        postalCode: null,
        country: "France",
        city: null,
        date: null,
        fiability: Fiability.MEDIUM,
        contact: null,
        email: null,
        phone: null,
        interveningCompany: null,
        notes: null,
        approverId: null,
        ...defaultValuesForm,
      };

  const form = useForm({
    mode: "onChange",
    defaultValues,
  });

  // Reset form with default values on mount (triggered by key change in parent)
  // This ensures that when the parent remounts this component with a new key,
  // the form is reset with the correct default values
  useEffect(() => {
    // Only reset on mount, not on subsequent renders
    // The key change in parent will cause a remount, so this runs once per mount
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = run only on mount

  const name = form.watch("name", defaultValues.name);
  const code = form.watch("code", defaultValues.code);
  const country = form.watch("country", defaultValues.country);
  const currentCompany = form.watch("company", defaultValues.company);
  const currentProject = form.watch("project", defaultValues.project);

  const fetchEdbs = useCallback(async () => {
    if (dockyard?._id) {
      let getEdbs;
      getEdbs = await queryClient.getQueryData([
        CacheInvalidationAction.GET_EDBS,
        { dockyard: dockyard._id },
      ]);
      if (!getEdbs) {
        getEdbs = await queryClient.fetchQuery({
          queryKey: [
            CacheInvalidationAction.GET_EDBS,
            { dockyard: dockyard._id },
          ],
          queryFn: () => EDBService.getEDBs({ dockyard: dockyard._id }),
          gcTime: 5 * 60 * 1000,
        });
      }
      setEdbs(getEdbs.data);
    }
  }, [dockyard?._id]);

  useEffect(() => {
    fetchEdbs();
  }, [fetchEdbs]);

  const hasEdbLinked = useMemo(
    () => dockyard?._id && edbs.length > 0,
    [dockyard?._id, edbs]
  );

  const {
    handleSubmit: handleSubmitIntervenant,
    togglePopin: toggleIntervenantPopin,
    isOpened: isIntervenantOpened,
    error: companyError,
    submitting: intervenantSubmitting,
  } = useCompanyForm();

  const fetchCompaniesAgencyCarrier = useCallback(async () => {
    if (currentCompany) {
      const filters = {
        contributorOf: currentCompany,
        isActive: true,
        companyTypes: CompanyType.CONTRIBUTOR,
      };
      let companies;
      companies = await queryClient.getQueryData([
        CacheInvalidationAction.GET_COMPANIES,
        filters,
      ]);
      if (!companies) {
        companies = await queryClient.fetchQuery({
          queryKey: [CacheInvalidationAction.GET_COMPANIES, filters],
          queryFn: () => CompanyServices.getCompanies(filters),
          gcTime: 60 * 1000,
        });
        companies = companies.data;
        await queryClient.setQueryData(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          companies
        );
      }
      setContributorOptions(companies);
    }
  }, [setContributorOptions, currentCompany]);

  useEffect(() => {
    fetchCompaniesAgencyCarrier();
  }, [fetchCompaniesAgencyCarrier]);

  const canEdit = isAdmin() || isClient();

  return {
    form,
    currentCompany,
    currentProject,
    name,
    code,
    country,
    edbs,
    hasEdbLinked,
    contributorOptions,
    isIntervenantOpened,
    toggleIntervenantPopin,
    handleSubmitIntervenant,
    companyError,
    intervenantSubmitting,
    canEdit,
  };
};
